import {
  Avatar,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { CaseCreationFormArr } from "./Data";
import { DropdownArrPropsType, FormDataType } from "DataTypes/Form";
import { commonFormSwitch, ControlledBoxSizeSwitch } from "Utils/SwitchCases";
import {
  FormatDropdownData,
  HandleFormChange,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  useSavedData,
  ValidateFormData,
} from "Utils/Common";
import ImgPath from "Constants/Imgs";
import { UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { Form, useNavigate } from "react-router-dom";
import { RouteUrls } from "Constants/RouteUrls";
import CaseApi from "Service/Case.api";
import { GlobalResType, HandleChangeProps } from "DataTypes/Common";
import { useDispatch } from "react-redux";
import {
  setAlertData,
  startLoading,
  stopLoading,
} from "../../../../../Redux/Screen/Action";
import ControlledForm from "Components/ControlledForm";
import ModalLayout from "Layouts/Modal";
import PaymentApi from "Service/Payment.api";

const CaseCreation: FC = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<FormDataType[]>(CaseCreationFormArr);
  const [bulkUpload, setBulkUpload] = useState<boolean>(false);
  const [errorObj, setErrorObj] = useState({
    csvError: false,
    csvErrorText: "",
    zipError: false,
    zipErrorText: "",
  });
  const [caseResponse, setCaseResponse] = useState<any>([]);
  const [selectedCSVFile, setSelectedCSVFile] = useState<File | null>(null);
  const [selectedZip, setSelectedZip] = useState<File | null>(null);
  const [consentGiven, setConsentGiven] = useState<"yes" | "no">("yes");
  const [isUploadSuccess, setIsUploadSuccess] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const navigate = useNavigate();

  // 1. Add state for reward usage
  const [useRewards, setUseRewards] = useState<boolean>(false);
  const [userPointsBalance, setUserPointsBalance] = useState<number>(0);
  const [isCreatingCase, setIsCreatingCase] = useState<boolean>(false);

  // 2. Fetch user points on load (Example)
  useEffect(() => {
    const fetchBalance = async () => {
      const res = await PaymentApi.getUserRewardBalance(myInfo.id || 0);
      if (res.success) setUserPointsBalance(res.data.balance);
    };
    fetchBalance();
  }, [myInfo.id]);

  useEffect(() => {
    handleOnLoad();
    // ----------------------------------------------------
    //  NEW: Cleanup Function (runs when component unmounts)
    // ----------------------------------------------------
    // return () => {
    // If the component is unmounting (user navigated away or closed the tab), 
    // clear the stored selection data.
    if (localStorage.getItem("userPurchaseData")) {
      localStorage.removeItem("userPurchaseData");
    }
    // };
  }, []);
  const getSavedData = useSavedData();

  const handleOnLoad: () => void = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
    try {
      const OrderTypeDrop: DropdownArrPropsType[] = await getOrderTypes();

      const res: GlobalResType = await CaseApi.fetchCaseTypes();

      let CaseTypeDrop: DropdownArrPropsType[] = [];

      if (res?.success) {
        setCaseResponse(res.data);
      }

      let orderTypeId: number | null = getSavedData()?.orderTypeId;
      let caseTypeId: number | null = getSavedData()?.caseTypeId;

      if (orderTypeId && res?.success) {
        const allCaseTypes = res.data; // use API data directly
        const filterArr = allCaseTypes.filter(
          (el: any) => el.orderTypeId === orderTypeId
        );
        CaseTypeDrop = FormatDropdownData(filterArr, "id", "name", "id");
      }

      setFormData((prev) =>
        prev.map((el) => {
          // skip description field
          if (el.id === "description") {
            return el;
          }
          if (el.id === "orderType") {
            const selectedOrder = OrderTypeDrop.find(
              (opt) => opt.value === orderTypeId
            );

            return {
              ...el,
              dropArr: OrderTypeDrop,
              value: selectedOrder?.value ?? "",
              dropValue: selectedOrder?.value ?? "",
            };
          }

          if (el.id === "caseType") {
            const selectedCase = CaseTypeDrop.find(
              (opt) => opt.value === caseTypeId
            );

            return {
              ...el,
              dropArr: CaseTypeDrop,
              value: selectedCase?.value ?? "",
              dropValue: selectedCase?.value ?? "",
            };
          }
          return el;
        })
      );
    } catch (error) {
      console.error("❌ Error in handleOnLoad:", error);
    } finally {
      dispatch(stopLoading());
    }
  };

  const getCaseTypes: (
    orderTypeId: number
  ) => Promise<DropdownArrPropsType[]> = async (orderTypeId) => {
    const filterArr = caseResponse.filter(
      (el: any) => el.orderTypeId === orderTypeId
    );
    return FormatDropdownData(filterArr, "id", "name", "id");
  };

  const getOrderTypes: () => Promise<DropdownArrPropsType[]> = async () => {
    const res: GlobalResType = await CaseApi.fetchOrderTypes();
    if (res?.success) {
      return FormatDropdownData(res?.data, "id", "name", "id");
    }
    return [];
  };

  const handleSubmit: () => void = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Creating case..." }));
    
    const payload = {
      clientId: myInfo.id,
      caseTypeId: formData[1]?.value,
      orderTypeId: formData[0]?.value,
      description: formData[2]?.value || "",
      isPurchaseWithRewards: useRewards
    };

    const validation = ValidateFormData(formData);
    setFormData(validation?.formData);
    if (!validation?.errorCheck) {
      if (isCreatingCase) return;
      setIsCreatingCase(true);
      try {
        const res = await CaseApi.createCase(payload);
        if (res?.success) {
          localStorage.removeItem("userPurchaseData");
          // handle here 0rs payment if amount is 0rs then not redirect to payment page
          if (res?.data?.casePrice === 0) {
            dispatch(stopLoading());
            navigate(`/chat/${res?.data?.orderTypeId}`);
            return;
          }
          dispatch(stopLoading());
          navigate(`${res?.data?.pkCase}/${RouteUrls.payment}`);
        }
      } finally {
        setIsCreatingCase(false);
        dispatch(stopLoading());
      }
    }
  };

  const handleChange: (data: HandleChangeProps) => void = async (data) => {
    const { id, value } = data;
    let newFormData: FormDataType[] = JSON.parse(JSON.stringify(formData));
    if (id === "orderType") {
      const orderTypeName = newFormData[0].dropArr?.find(
        (el) => el.value === value
      )?.label;

      if (
        orderTypeName === "Online Mediation" ||
        orderTypeName === "Online Arbitration"
      ) {
        setBulkUpload(true);
        if (newFormData.length > 1) {
          newFormData.splice(1, 2);
        }
      } else {
        setBulkUpload(false);
        if (newFormData.length === 1) {
          newFormData.push({ ...CaseCreationFormArr[1] });
          newFormData.push({ ...CaseCreationFormArr[2] });
        }
        const orderTypeId = typeof value === "number" ? value : 0;
        const caseTypeDrop = await getCaseTypes(orderTypeId);
        if (newFormData[1]) {
          newFormData[1].dropArr = caseTypeDrop;
          newFormData[1].value = "";
        }
        if (newFormData[2]) {
          newFormData[2].value = "";
        }
      }
    }
    const updatedFormData = HandleFormChange(data, newFormData);
    setFormData(updatedFormData);
  };

  const handleDocumentUpload: () => void = async () => {
    let errors = {
      csvError: false,
      csvErrorText: "",
      zipError: false,
      zipErrorText: "",
    };
    if (!selectedCSVFile) {
      errors.csvError = true;
      errors.csvErrorText = "Please select a CSV file";
    }

    if (!selectedZip) {
      errors.zipError = true;
      errors.zipErrorText = "Please select a ZIP file";
    }
    setErrorObj(errors);

    // If there are any errors, stop execution
    if (errors.csvError || errors.zipError) return;

    if (selectedCSVFile && selectedZip) {

      //validate file size
      if (selectedCSVFile.size > MAX_FILE_SIZE_BYTES) {
        setErrorObj({
          ...errorObj,
          csvError: true,
          csvErrorText: `CSV file size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
        });
        return;
      }

      if (selectedZip.size > MAX_FILE_SIZE_BYTES) {
        setErrorObj({
          ...errorObj,
          zipError: true,
          zipErrorText: `ZIP file size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
        });
        return;
      }
      const multipartFormData = new FormData();
      multipartFormData.append("csvFile", selectedCSVFile);
      multipartFormData.append("zipFile", selectedZip);
      multipartFormData.append(
        "requestDto",
        JSON.stringify({
          orderTypeId: formData[0]?.value,
          description: "",
          csvFileName: selectedCSVFile?.name,
          zipFileName: selectedZip?.name,
          batchNumber: "",
          isBulkUpload: true,
          isConsentGivenToUs: consentGiven === "yes" ? true : false,
          clientId: myInfo?.id,
        })
      );
      if (isCreatingCase) return;
      setIsCreatingCase(true);
      dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
      try {
        const res = await CaseApi.DocumentBulkUpload(multipartFormData);
        if (res?.success) {
          setIsUploadSuccess(true);
          // NEW: Remove the data after successful bulk upload and before navigation
          localStorage.removeItem("userPurchaseData");
          dispatch(
            setAlertData({
              alertMessage:
                "We have received your uploaded files, Support team will get back to you soon.",
              alertVariant: "success",
              openAlert: true,
            })
          );
          navigate(`/${RouteUrls.dashboard}`);
        }
      } finally {
        setIsCreatingCase(false);
        dispatch(stopLoading());
      }
    }
  };

  const handleFormValidation = () => {
    const validation = ValidateFormData(formData);
    setFormData(validation?.formData);
    if (validation?.errorCheck === false) {
      setModalOpen(true);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <ModalLayout
        content={
          <Typography>Are you sure want to create a new case?</Typography>
        }
        open={modalOpen}
        modwidth="400px"
        onClose={() => setModalOpen(false)}
        heading="New Case"
        buttonArr={[
          {
            id: "createCase",
            label: isCreatingCase ? "Loading..." : "Create Case",
            clickHandler: bulkUpload ? handleDocumentUpload : handleSubmit,
            disable: isCreatingCase,
          },
          {
            id: "cancel",
            label: "Cancel",
            clickHandler: () => setModalOpen(false),
            type: "border",
            disable: isCreatingCase,
          },
        ]}
      />
      <Avatar src={ImgPath.FileCaseIcon} sx={{ m: 1 }}></Avatar>
      <Typography
        component="h1"
        color="primary.main"
        variant="h5"
        sx={{ mb: 2 }}
      >
        Order Creation
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          gap: { xs: 2, md: 4 },
          px: { xs: 2, md: 0 },
          maxWidth: { xs: "100%" },
          mx: "auto",
        }}
      >
        <Box
          sx={{
            mx: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            flex: "1 1 65%",
            maxWidth: { xs: "100%", md: "65%" },
          }}
        >
          {/* <Avatar src={ImgPath.FileCaseIcon} sx={{ m: 1 }}></Avatar>
          <Typography
            component="h1"
            color="primary.main"
            variant="h5"
            sx={{ mb: 2 }}
          >
            Case Creation
          </Typography> */}

          <Grid
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              width: { xs: "100%", md: "60%" },
            }}
            container
            spacing={2}
            justifyContent="center"
          >
            {formData.map((el: FormDataType) => (
              <Grid key={el.id} {...ControlledBoxSizeSwitch(el.boxSize)}>
                {commonFormSwitch({
                  formObj: el,
                  onChange: (data) => handleChange(data),
                })}
              </Grid>
            ))}
          </Grid>

          {bulkUpload && (
            <Grid
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                width: { xs: "100%", md: "60%" },
                mt: 1,
              }}
              container
              spacing={2}
              justifyContent="center"
            >
              <Grid size={{ md: 12, lg: 12 }}>
                <ControlledForm.Input
                  id="csvSelection"
                  type="file"
                  onChange={(value) =>
                    setSelectedCSVFile(
                      typeof value == "object" ? value[0] : null
                    )
                  }
                  acceptFileTypes="text/csv"
                  required={true}
                  label="Upload CSV Document"
                  focused={true}
                  readOnly={isUploadSuccess}
                  error={errorObj.csvError}
                  errorText={errorObj.csvErrorText}
                />
                <Box mt={2}>
                  <ControlledForm.Input
                    id="zipSelection"
                    type="file"
                    acceptFileTypes="application/zip"
                    required={true}
                    onChange={(value) =>
                      setSelectedZip(typeof value == "object" ? value[0] : null)
                    }
                    label="Upload ZIP File"
                    focused={true}
                    readOnly={isUploadSuccess}
                    error={errorObj.zipError}
                    errorText={errorObj.zipErrorText}
                  />
                </Box>
                {/* <Box mt={2} textAlign="center">
                <Button
                  variant="contained"
                  disabled={isUploadSuccess}
                  onClick={handleDocumentUpload}
                >
                  {isUploadSuccess ? "Upload Successfull" : "Upload"}
                </Button>
              </Box> */}
                <Typography
                  align="center" // centers the text
                  mb={1}
                  mt={1}
                >
                  Do you have the Consent to file with us?
                </Typography>
                <Box
                  mt={2}
                  textAlign="center"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    name="radio-buttons-group"
                    value={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.value as any)}
                    sx={{
                      alignContent: "center",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <FormControlLabel
                      disabled={isUploadSuccess}
                      value="yes"
                      control={<Radio sx={sxRadio} />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={isUploadSuccess}
                      value="no"
                      control={<Radio sx={sxRadio} />}
                      label="No"
                    />
                  </RadioGroup>
                </Box>
              </Grid>
            </Grid>
          )}

          <Box mt={3}>
            <Button
              variant="contained"
              // onClick={bulkUpload ? handleDocumentUpload : handleSubmit}
              onClick={handleFormValidation}
            >
              Submit
            </Button>
          </Box>
        </Box>

        <Box
          className="summaryContainer"
          sx={{
            width: { xs: "100%", md: "30%" },
            ml: { xs: 0, md: 2 },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {/* Order Summary Card */}
          <Box
            sx={{
              width: "100%",
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 2,
              p: 2,
              bgcolor: "background.paper",
              position: { xs: "static", md: "sticky" },
              top: { md: 120 },
            }}
          >
            <Typography
              sx={{ textAlign: "center" }}
              variant="h6"
              color="text.primary"
              gutterBottom
            >
              Order Summary
            </Typography>

            {/* compute labels and amounts from formData / caseResponse */}
            {(() => {
              const orderVal = formData?.[0]?.value;
              const orderTypeLabel = formData?.[0]?.dropArr?.find((d: any) => d.value === orderVal)?.label ?? "--";

              const caseVal = formData?.[1]?.value;
              const caseTypeLabel = formData?.[1]?.dropArr?.find((d: any) => d.value === caseVal)?.label ?? "--";

              let baseAmount = 0;
              let gstAmount = 0;
              let finalAmount = 0;

              const POINT_VALUE = 1;
              let pointsDiscount = 0;

              if (caseVal) {
                const matched = caseResponse?.find((c: any) => String(c?.id) === String(caseVal));
                if (matched) {
                  baseAmount = matched.casePrice || 0;
                  if (baseAmount > 0) {
                    gstAmount = baseAmount * 0.18;
                    finalAmount = baseAmount + gstAmount;
                  } else {
                    finalAmount = baseAmount;
                  }
                }
              }

              // 2. Calculate Discount if rewards are toggled
              if (useRewards && userPointsBalance > 0) {
                const maxDiscountPossible = userPointsBalance * POINT_VALUE;
                const maxAllowedDiscount = baseAmount * 0.25;
                pointsDiscount = Math.min(maxDiscountPossible, maxAllowedDiscount);
              }

              const finalPayable = finalAmount - pointsDiscount;

              return (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography>Order Type:</Typography>
                    <Typography fontWeight="500">{orderTypeLabel}</Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography>Case Type:</Typography>
                    <Typography fontWeight="500">{caseTypeLabel}</Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography>Base Price:</Typography>
                    <Typography>{baseAmount > 0 ? `₹${baseAmount.toLocaleString()}` : "--"}</Typography>
                  </Box>

                  {/* Only show GST if price is greater than 0 */}
                  {baseAmount > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography>GST (18%):</Typography>
                      <Typography>₹{gstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  )}

                  {/* 3. Reward Points Section */}
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(162, 72, 87, 0.05)', borderRadius: 1, border: '1px dashed #a24857' }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={useRewards}
                          onClick={() => setUseRewards(!useRewards)}
                          disabled={userPointsBalance <= 0 || finalAmount <= 0}
                          sx={sxRadio}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="600">Apply Reward Points (Max 25%)</Typography>
                          <Typography variant="caption" color="text.primary">Available: {userPointsBalance} pts</Typography>
                        </Box>
                      }
                    />

                    {useRewards && pointsDiscount > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, color: "success.main" }}>
                        <Typography variant="body2">Points Discount:</Typography>
                        <Typography variant="body2">- ₹{pointsDiscount.toLocaleString()}</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* 4. Final Total Calculation */}
                  {/* <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, pt: 1, borderTop: "1px solid #eee" }}>
                    <Typography variant="subtitle1" fontWeight="bold">Total Amount:</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                      {finalAmount > 0 ? `₹${finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "Free"}
                    </Typography>
                  </Box> */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, pt: 1, borderTop: "2px solid #eee" }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">Total Payable:</Typography>
                      {/* {useRewards && <Typography variant="caption" color="text.primary">Net of rewards</Typography>} */}
                    </Box>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {finalPayable > 0 ? `₹${finalPayable.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "Free"}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CaseCreation;

const sxRadio = {
  color: "#a24857", // default outline color
  "&.Mui-checked": {
    color: "#a24857", // fill when selected
  },
  "&:hover": {
    backgroundColor: "rgba(128,0,0,0.08)", // maroon hover effect
  },
};
